import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import CopyToolbar from '../components/DropChecker/CopyToolbar';
import { dropCheckerApi } from '../api';
import toast from 'react-hot-toast';
import { FiPlay, FiSquare, FiCopy, FiSave, FiCheckCircle, FiAlertTriangle, FiMail, FiRefreshCw } from 'react-icons/fi';
import {
    inputClass,
    btnPrimary,
    btnGhost,
    iconBtn,
    th,
    tabBase,
    tabOn,
    tabOff,
    checkboxClass,
    verdictPill,
    verdictLabel,
    orderStatusPill,
    jobStatusPill,
    shortDateTime,
    copyText,
    n
} from '../components/DropChecker/ui';

const emptyForm = {
    panel_base_url: '',
    panel_api_key: '',
    scraper_base_url: '',
    scraper_api_key: '',
    service_id: '',
    window_days: 10,
    drop_threshold: 1,
    batch_size: 10,
    metric_override: '',
    notify_email: '',
    scan_statuses: ['completed', 'partial']
};

export default function DropCheckerPage() {
    const [form, setForm] = useState(emptyForm);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState('');           // '', 'fetch', 'count'
    const [testing, setTesting] = useState('');     // '', 'panel', 'scraper'
    const [testUrl, setTestUrl] = useState('');
    const [orders, setOrders] = useState([]);
    const [results, setResults] = useState({});
    const [selected, setSelected] = useState([]);
    const [tab, setTab] = useState('dropped');
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [runInfo, setRunInfo] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [jobsError, setJobsError] = useState('');
    const [openingJob, setOpeningJob] = useState(null);
    const cancelRef = useRef(false);

    const applySettings = useCallback(row => {
        setMeta(row);
        setForm({
            panel_base_url: row.panel_base_url || '',
            panel_api_key: '',
            scraper_base_url: row.scraper_base_url || '',
            scraper_api_key: '',
            service_id: row.service_id || '',
            window_days: row.window_days || 10,
            drop_threshold: row.drop_threshold || 1,
            batch_size: row.batch_size || 10,
            metric_override: row.metric_override || '',
            notify_email: row.notify_email || '',
            scan_statuses: row.scan_statuses || ['completed', 'partial']
        });
    }, []);

    const load = useCallback(async () => {
        try {
            const { data } = await dropCheckerApi.getSettings();
            applySettings(data.settings);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to load Drop Checker settings');
        } finally {
            setLoading(false);
        }
    }, [applySettings]);

    useEffect(() => { load(); }, [load]);

    const loadJobs = useCallback(async () => {
        try {
            const { data } = await dropCheckerApi.listJobs();
            setJobs(data.jobs || []);
            setJobsError('');
        } catch (err) {
            setJobsError(err.response?.data?.error || 'Could not load background runs');
        }
    }, []);

    useEffect(() => { loadJobs(); }, [loadJobs]);

    // While a run is in flight, refresh the counters so progress moves.
    const hasRunningJob = jobs.some(job => job.status === 'running');
    useEffect(() => {
        if (!hasRunningJob) return undefined;
        const timer = setInterval(loadJobs, 5000);
        return () => clearInterval(timer);
    }, [hasRunningJob, loadJobs]);

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const saveSettings = async (silent) => {
        if (!silent) setSaving(true);
        try {
            const { data } = await dropCheckerApi.updateSettings(form);
            applySettings(data.settings);
            if (!silent) toast.success('Settings saved');
            return true;
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to save settings');
            return false;
        } finally {
            if (!silent) setSaving(false);
        }
    };

    const resetRun = () => {
        setOrders([]);
        setResults({});
        setSelected([]);
        setRunInfo(null);
        setProgress({ done: 0, total: 0 });
    };

    /** Live counts for the scanned orders, in small batches, serially. */
    const countLive = async (orderList, runMeta) => {
        cancelRef.current = false;
        setBusy('count');
        setProgress({ done: 0, total: orderList.length });

        const size = Math.max(5, parseInt(form.batch_size, 10) || 10);
        const seen = {};

        for (let i = 0; i < orderList.length; i += size) {
            if (cancelRef.current) break;
            const batch = orderList.slice(i, i + size);
            try {
                const { data } = await dropCheckerApi.liveCounts({
                    metric: runMeta.metric,
                    threshold: runMeta.threshold,
                    orders: batch.map(order => ({
                        id: order.id,
                        link: order.link,
                        expected: order.expected,
                        service_name: order.service_name,
                        metric: order.metric
                    }))
                });
                (data.rows || []).forEach(row => { seen[row.id] = row; });
            } catch (err) {
                toast.error(err.response?.data?.error || 'Live count check failed for a batch');
                break;
            }
            setResults({ ...seen });
            setProgress({ done: Math.min(i + batch.length, orderList.length), total: orderList.length });
        }

        setBusy('');
        if (cancelRef.current) toast('Stopped — the list shows what was checked so far');
        return seen;
    };

    const runScan = async (countToo = true) => {
        resetRun();
        setBusy('fetch');

        const saved = await saveSettings(true);
        if (!saved) {
            setBusy('');
            return;
        }

        let data;
        try {
            ({ data } = await dropCheckerApi.scanOrders({
                service_id: form.service_id,
                window_days: form.window_days,
                scan_statuses: form.scan_statuses,
                threshold: form.drop_threshold,
                metric: form.metric_override || null
            }));
        } catch (err) {
            setBusy('');
            toast.error(err.response?.data?.error || 'Could not fetch orders from the panel');
            return;
        }

        setBusy('');
        setOrders(data.orders || []);
        setRunInfo({
            serviceId: data.service_id,
            count: data.count,
            days: data.window?.days,
            threshold: data.threshold,
            metric: data.metric,
            statuses: data.statuses || []
        });

        if (!data.count) {
            toast('No orders matched. Try a wider window or another status.');
            return;
        }
        toast.success(`Fetched ${data.count} order(s)`);

        if (countToo) {
            const seen = await countLive(data.orders || [], { metric: data.metric, threshold: data.threshold });
            // Pre-select what the operator is here for; the toolbar copies the selection.
            setSelected((data.orders || []).filter(o => seen[o.id] && seen[o.id].dropped).map(o => o.id));
        }
    };

    const stop = () => {
        cancelRef.current = true;
    };

    const startBackgroundRun = async () => {
        if (!String(form.notify_email).trim()) {
            toast.error('Add an email address so the result can reach you');
            return;
        }
        const saved = await saveSettings(true);
        if (!saved) return;

        setBusy('background');
        try {
            const { data } = await dropCheckerApi.startBackground({
                service_id: form.service_id,
                window_days: form.window_days,
                scan_statuses: form.scan_statuses,
                threshold: form.drop_threshold,
                metric: form.metric_override || null,
                notify_email: form.notify_email
            });
            toast.success(`Running in the background — email goes to ${data.notify_email}`);
            loadJobs();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not start the background check');
        } finally {
            setBusy('');
        }
    };

    const openJob = async (id) => {
        setOpeningJob(id);
        try {
            const { data } = await dropCheckerApi.getJob(id);
            const job = data.job;
            const jobRows = Array.isArray(job.results) ? job.results : [];

            setOrders(jobRows);
            const map = {};
            jobRows.forEach(row => { map[row.id] = row; });
            setResults(map);
            setRunInfo({
                serviceId: job.service_id,
                count: job.total,
                days: job.window_days,
                threshold: job.threshold,
                metric: job.metric,
                statuses: (job.scan_statuses || '').split(',').filter(Boolean)
            });
            setSelected(jobRows.filter(row => row.dropped).map(row => row.id));
            setTab('dropped');

            if (!jobRows.length) toast('This run has no stored rows yet');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not load that run');
        } finally {
            setOpeningJob(null);
        }
    };

    const stopJobRun = async (id) => {
        try {
            await dropCheckerApi.stopJob(id);
            toast('Stopping after the batch in progress…');
            loadJobs();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not stop that run');
        }
    };

    const testPanel = async () => {
        setTesting('panel');
        try {
            await saveSettings(true);
            const { data } = await dropCheckerApi.testPanel();
            if (data.success) toast.success(data.message || 'Panel answered');
            else toast.error(data.error || 'Panel test failed');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Panel test failed');
        } finally {
            setTesting('');
        }
    };

    const testScraper = async () => {
        if (!testUrl.trim()) return toast.error('Enter a profile or post URL to test');
        setTesting('scraper');
        try {
            await saveSettings(true);
            const { data } = await dropCheckerApi.testScraper({ url: testUrl.trim() });
            if (data.success) toast.success(`${data.metric}: ${data.result.current} (${data.result.source || data.result.kind})`);
            else toast.error(data.error || 'Count API test failed');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Count API test failed');
        } finally {
            setTesting('');
        }
    };

    const rows = useMemo(
        () => orders.map(order => ({ ...order, ...(results[order.id] || {}), checked: !!results[order.id] })),
        [orders, results]
    );

    const flagged = useMemo(() => rows.filter(row => row.dropped), [rows]);
    const unknown = useMemo(() => rows.filter(row => row.checked && row.unknown), [rows]);
    const visible = useMemo(() => {
        if (tab === 'dropped') return flagged;
        if (tab === 'unknown') return unknown;
        return rows;
    }, [tab, flagged, unknown, rows]);

    const selectedRows = useMemo(
        () => rows.filter(row => selected.indexOf(row.id) !== -1),
        [rows, selected]
    );

    const toggle = id => setSelected(prev => (
        prev.indexOf(id) !== -1 ? prev.filter(x => x !== id) : [...prev, id]
    ));

    const allVisibleSelected = visible.length > 0 && visible.every(row => selected.indexOf(row.id) !== -1);

    const toggleAllVisible = () => {
        if (allVisibleSelected) {
            const ids = visible.map(row => row.id);
            setSelected(prev => prev.filter(id => ids.indexOf(id) === -1));
        } else {
            setSelected(prev => [...new Set([...prev, ...visible.map(row => row.id)])]);
        }
    };

    const summary = useMemo(() => {
        const checked = rows.filter(row => row.checked).length;
        return {
            checked,
            ok: checked - flagged.length - unknown.length,
            dropped: flagged.length,
            unknown: unknown.length
        };
    }, [rows, flagged, unknown]);

    const running = busy === 'count';
    const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="mb-6">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Drop Checker</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    Find orders whose live count fell below what they delivered, then copy the list for the provider.
                </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
                {/* Panel ------------------------------------------------------ */}
                <div className="card p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                        <h2 className="text-sm font-semibold text-foreground">Panel (Admin API v2)</h2>
                        <button type="button" onClick={testPanel} className={btnGhost} disabled={!!testing}>
                            {testing === 'panel' ? 'Testing…' : 'Test panel'}
                        </button>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Base URL</label>
                        <input
                            type="url"
                            className={inputClass}
                            value={form.panel_base_url}
                            onChange={e => set('panel_base_url', e.target.value)}
                            placeholder="https://smmpanelone.com"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">API key</label>
                        <input
                            type="text"
                            className={inputClass}
                            value={form.panel_api_key}
                            onChange={e => set('panel_api_key', e.target.value)}
                            placeholder={meta?.has_panel_api_key
                                ? `${meta.panel_api_key_mask} saved — type to replace`
                                : 'X-Api-Key from the panel admin'}
                        />
                        <p className="text-xs text-muted-foreground">
                            {meta?.has_panel_api_key
                                ? `Stored encrypted (${meta.panel_api_key_source === 'env' ? 'from .env' : 'in the manager database'}).`
                                : 'Stored encrypted, never shown again.'}
                        </p>
                    </div>
                </div>

                {/* Count API -------------------------------------------------- */}
                <div className="card p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                        <h2 className="text-sm font-semibold text-foreground">Count API</h2>
                        <button type="button" onClick={testScraper} className={btnGhost} disabled={!!testing}>
                            {testing === 'scraper' ? 'Testing…' : 'Test count API'}
                        </button>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Base URL</label>
                        <input
                            type="url"
                            className={inputClass}
                            value={form.scraper_base_url}
                            onChange={e => set('scraper_base_url', e.target.value)}
                            placeholder="https://scrape.smmpanelperfect.com"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">API key</label>
                        <input
                            type="text"
                            className={inputClass}
                            value={form.scraper_api_key}
                            onChange={e => set('scraper_api_key', e.target.value)}
                            placeholder={meta?.has_scraper_api_key
                                ? `${meta.scraper_api_key_mask} saved — type to replace`
                                : 'Count API key'}
                        />
                        <p className="text-xs text-muted-foreground">
                            {meta?.has_scraper_api_key
                                ? `Ready to use (${meta.scraper_api_key_source === 'env' ? 'from the server .env' : 'stored in the manager database'}).`
                                : 'Set once — stored encrypted.'}
                        </p>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Test URL</label>
                        <input
                            type="text"
                            className={inputClass}
                            value={testUrl}
                            onChange={e => setTestUrl(e.target.value)}
                            placeholder="https://www.instagram.com/someprofile"
                        />
                    </div>
                </div>
            </div>

            {/* Scan settings ------------------------------------------------ */}
            <div className="card p-4 space-y-4 mb-4">
                <h2 className="text-sm font-semibold text-foreground">Scan settings</h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Service ID(s)</label>
                        <input
                            type="text"
                            className={inputClass}
                            value={form.service_id}
                            onChange={e => set('service_id', e.target.value)}
                            placeholder="1234 or 1234, 5678"
                        />
                        <p className="text-xs text-muted-foreground">Comma-separate to check several services in one run.</p>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last (days)</label>
                        <input
                            type="number"
                            min="1"
                            max={meta?.max_window_days || 90}
                            className={inputClass}
                            value={form.window_days}
                            onChange={e => set('window_days', e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Flag if missing ≥</label>
                        <input
                            type="number"
                            min="1"
                            className={inputClass}
                            value={form.drop_threshold}
                            onChange={e => set('drop_threshold', e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Batch size</label>
                        <input
                            type="number"
                            min="5"
                            max={meta?.max_batch || 50}
                            className={inputClass}
                            value={form.batch_size}
                            onChange={e => set('batch_size', e.target.value)}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-2">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Order statuses</span>
                        <div className="flex flex-wrap gap-3 pt-1">
                            {(meta?.status_options || []).map(status => (
                                <label key={status} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className={checkboxClass}
                                        checked={form.scan_statuses.indexOf(status) !== -1}
                                        onChange={e => set('scan_statuses', e.target.checked
                                            ? [...form.scan_statuses, status]
                                            : form.scan_statuses.filter(s => s !== status))}
                                    />
                                    {status}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">What to count</label>
                        <select
                            className={inputClass}
                            value={form.metric_override}
                            onChange={e => set('metric_override', e.target.value)}
                        >
                            <option value="">Auto (from the service name and link)</option>
                            {(meta?.metric_options || []).map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email when finished</label>
                        <input
                            type="email"
                            className={inputClass}
                            value={form.notify_email}
                            onChange={e => set('notify_email', e.target.value)}
                            placeholder="you@example.com"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                    <button type="button" onClick={() => saveSettings(false)} className={btnGhost} disabled={saving || !!busy}>
                        <FiSave size={14} /> {saving ? 'Saving…' : 'Save settings'}
                    </button>
                    <button type="button" onClick={() => runScan(true)} className={btnPrimary} disabled={!!busy}>
                        <FiPlay size={14} /> {busy === 'fetch' ? 'Fetching orders…' : 'Check for drops'}
                    </button>
                    <button type="button" onClick={() => runScan(false)} className={btnGhost} disabled={!!busy}>
                        Fetch orders only
                    </button>
                    <button
                        type="button"
                        onClick={startBackgroundRun}
                        className={btnGhost}
                        disabled={!!busy || hasRunningJob}
                        title="Runs on the server — you can close this tab"
                    >
                        <FiMail size={14} /> {busy === 'background' ? 'Starting…' : 'Check in background & email me'}
                    </button>
                    {running && (
                        <button type="button" onClick={stop} className={btnGhost}>
                            <FiSquare size={14} /> Stop
                        </button>
                    )}
                </div>

                <p className="text-xs text-muted-foreground">
                    One run covers up to {meta?.max_scan_orders || 3000} orders and up to {meta?.max_window_days || 90} days
                    back, and multiple service IDs share that same budget. Each order costs one live-count lookup
                    (roughly a second or two, some links take much longer), so a few hundred orders take minutes — use
                    the background option for those and the email will bring you back.
                </p>

                {running && (
                    <div className="space-y-1">
                        <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary transition-all" style={{ width: `${percent}%` }}></div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Checking live counts — {progress.done}/{progress.total} ({percent}%)
                        </p>
                    </div>
                )}
            </div>

            {/* Background runs --------------------------------------------- */}
            {(jobs.length > 0 || jobsError) && (
                <div className="card p-4 mb-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div>
                            <h2 className="text-sm font-semibold text-foreground">Background runs</h2>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Server-side checks — open one to load its results into the table below.
                            </p>
                        </div>
                        <button type="button" onClick={loadJobs} className={btnGhost}>
                            <FiRefreshCw size={14} /> Refresh
                        </button>
                    </div>

                    {jobsError && <p className="text-xs text-destructive mb-2">{jobsError}</p>}

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/20">
                                    <th className={th}>Run</th>
                                    <th className={th}>Service</th>
                                    <th className={th}>Status</th>
                                    <th className={th}>Progress</th>
                                    <th className={th}>Dropped</th>
                                    <th className={`${th} hidden md:table-cell`}>Unknown</th>
                                    <th className={`${th} hidden md:table-cell`}>Started</th>
                                    <th className={th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="[&_tr:last-child]:border-0">
                                {jobs.map(job => {
                                    const total = job.total || 0;
                                    const done = job.checked || 0;
                                    const pct = total ? Math.round((done / total) * 100) : 0;
                                    return (
                                        <tr key={job.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                                            <td className="p-3 align-middle font-mono text-xs text-foreground">#{job.id}</td>
                                            <td className="p-3 align-middle font-mono text-xs text-foreground">{job.service_id || '—'}</td>
                                            <td className="p-3 align-middle">
                                                <span className={jobStatusPill(job.status)}>{job.status}</span>
                                                {job.status === 'failed' && job.error_message && (
                                                    <span className="block text-xs text-muted-foreground mt-1 max-w-[240px] break-words">
                                                        {job.error_message}
                                                    </span>
                                                )}
                                                {job.notify_email && (
                                                    <span className="block text-xs text-muted-foreground mt-1 break-all">
                                                        {job.emailed_at ? 'emailed ' : 'email: '}{job.notify_email}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3 align-middle min-w-[140px]">
                                                <div className="text-xs text-muted-foreground tabular-nums mb-1">
                                                    {done}/{total || '—'}
                                                </div>
                                                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full ${job.status === 'running' ? 'bg-primary' : job.status === 'failed' ? 'bg-destructive' : 'bg-green-600'}`}
                                                        style={{ width: `${pct}%` }}
                                                    ></div>
                                                </div>
                                            </td>
                                            <td className="p-3 align-middle tabular-nums text-red-600 dark:text-red-400">{job.dropped_count || 0}</td>
                                            <td className="p-3 align-middle hidden md:table-cell tabular-nums text-amber-600 dark:text-amber-400">{job.unknown_count || 0}</td>
                                            <td className="p-3 align-middle hidden md:table-cell text-xs text-muted-foreground">{shortDateTime(job.started_at)}</td>
                                            <td className="p-3 align-middle">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        className={btnGhost}
                                                        onClick={() => openJob(job.id)}
                                                        disabled={openingJob === job.id}
                                                    >
                                                        {openingJob === job.id ? 'Opening…' : 'Open results'}
                                                    </button>
                                                    {job.status === 'running' && (
                                                        <button type="button" className={btnGhost} onClick={() => stopJobRun(job.id)}>
                                                            Stop
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Results ------------------------------------------------------ */}
            {!!orders.length && (
                <>
                    <div className="card p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="space-y-1 min-w-0">
                            <p className="text-sm text-foreground">
                                Service <span className="font-mono">{runInfo?.serviceId}</span> · last {runInfo?.days} day(s) ·{' '}
                                {runInfo?.statuses?.join(', ')} · metric {runInfo?.metric || 'per order'} · flag ≥ {runInfo?.threshold}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                <span>Scanned {summary.checked}/{rows.length}</span>
                                <span className="text-red-600 dark:text-red-400">Dropped {summary.dropped}</span>
                                <span className="text-amber-600 dark:text-amber-400">Unknown {summary.unknown}</span>
                                <span className="text-green-700 dark:text-green-400">OK {summary.ok}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {[
                                { key: 'dropped', label: `Dropped (${flagged.length})` },
                                { key: 'unknown', label: `Unknown (${unknown.length})` },
                                { key: 'all', label: `All (${rows.length})` }
                            ].map(item => (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => setTab(item.key)}
                                    className={`${tabBase} ${tab === item.key ? tabOn : tabOff}`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="card overflow-hidden mb-4">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-muted/20">
                                        <th className={`${th} w-10`}>
                                            <input
                                                type="checkbox"
                                                className={checkboxClass}
                                                checked={allVisibleSelected}
                                                onChange={toggleAllVisible}
                                                aria-label="Select all visible"
                                            />
                                        </th>
                                        <th className={th}>Order</th>
                                        <th className={th}>External ID</th>
                                        <th className={th}>Link</th>
                                        <th className={`${th} hidden md:table-cell`}>Status</th>
                                        <th className={th}>Start</th>
                                        <th className={th}>Qty</th>
                                        <th className={`${th} hidden md:table-cell`}>Remains</th>
                                        <th className={th}>Live count</th>
                                        <th className={th}>Missing</th>
                                        <th className={th}>Verdict</th>
                                    </tr>
                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                    {visible.map(row => (
                                        <tr key={row.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                                            <td className="p-3 align-middle">
                                                <input
                                                    type="checkbox"
                                                    className={checkboxClass}
                                                    checked={selected.indexOf(row.id) !== -1}
                                                    onChange={() => toggle(row.id)}
                                                    aria-label={`Select order ${row.id}`}
                                                />
                                            </td>
                                            <td className="p-3 align-middle font-mono text-xs text-foreground">{row.id}</td>
                                            <td className="p-3 align-middle">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="font-mono text-xs text-foreground">{row.external_id || '—'}</span>
                                                    {!!row.external_id && (
                                                        <button
                                                            type="button"
                                                            className={iconBtn}
                                                            title="Copy external ID"
                                                            onClick={async () => {
                                                                const ok = await copyText(row.external_id);
                                                                if (ok) toast.success('External ID copied');
                                                                else toast.error('Copy failed');
                                                            }}
                                                        >
                                                            <FiCopy size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3 align-middle min-w-[200px]">
                                                <a
                                                    href={row.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs text-primary hover:underline break-all inline-block max-w-[260px]"
                                                >
                                                    {row.link || '—'}
                                                </a>
                                            </td>
                                            <td className="p-3 align-middle hidden md:table-cell">
                                                <span className={orderStatusPill(row.status)}>{row.status || '—'}</span>
                                            </td>
                                            <td className="p-3 align-middle tabular-nums">{n(row.start_count)}</td>
                                            <td className="p-3 align-middle tabular-nums">{n(row.quantity)}</td>
                                            <td className="p-3 align-middle hidden md:table-cell tabular-nums">{n(row.remains)}</td>
                                            <td className="p-3 align-middle tabular-nums">{n(row.current)}</td>
                                            <td className={`p-3 align-middle tabular-nums ${row.dropped ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                                                {n(row.missing)}
                                            </td>
                                            <td className="p-3 align-middle">
                                                <span className={verdictPill(row)}>{verdictLabel(row)}</span>
                                            </td>
                                        </tr>
                                    ))}
                                    {!visible.length && (
                                        <tr>
                                            <td colSpan={11} className="p-8 text-center text-muted-foreground">
                                                {tab === 'dropped'
                                                    ? 'No drops found in this run.'
                                                    : tab === 'unknown'
                                                        ? 'Every order was verified.'
                                                        : 'Nothing here yet.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {!!summary.dropped && (
                        <p className="flex items-start gap-2 text-xs text-muted-foreground mb-4">
                            <FiAlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                            Missing is measured against start count + delivered, so a link carrying several orders is
                            compared once per order. Rows marked Unknown could not be read — check them by hand before
                            claiming.
                        </p>
                    )}
                    {!summary.dropped && !!summary.checked && (
                        <p className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                            <FiCheckCircle size={14} /> No order is short by the threshold you set.
                        </p>
                    )}

                    <CopyToolbar flagged={flagged} selected={selectedRows} />
                </>
            )}

            {!orders.length && !busy && (
                <div className="card p-8 text-center text-muted-foreground text-sm">
                    Set the panel and count API above, then press <span className="text-foreground font-medium">Check for drops</span>.
                </div>
            )}
        </DashboardLayout>
    );
}
